/**
 * Mahsulot sotish o‘lchovi: ko'p birlikli (piece, kg, meter, liter, box, ...).
 * Savatda: price = birlik narxi (dona uchun dona, kg/m uchun 1 kg yoki 1 m),
 * qty = miqdor (butun yoki o‘nlik), jami = round(price * qty).
 */

export const SALE_UNIT_PIECE = 'piece';
export const SALE_UNIT_KG = 'kg';
export const SALE_UNIT_METER = 'meter';
/** Optom / ulgurji — donaga o‘xshash miqdor (vaznli emas) */
export const SALE_UNIT_WHOLESALE = 'wholesale';
export const SALE_UNIT_LITER = 'liter';
export const SALE_UNIT_TON = 'ton';
export const SALE_UNIT_CUBIC_METER = 'cubic_meter';
export const SALE_UNIT_CM = 'cm';
export const SALE_UNIT_MM = 'mm';
export const SALE_UNIT_PAIR = 'pair';
export const SALE_UNIT_SIZE = 'size';

const WEIGHTED = new Set([SALE_UNIT_KG, SALE_UNIT_METER, SALE_UNIT_LITER, SALE_UNIT_TON, SALE_UNIT_CUBIC_METER, 'sqm', SALE_UNIT_CM, SALE_UNIT_MM]);

/**
 * Mahsulot formasidagi barcha tanlangan sotish o‘lchovlari (size dan tashqari), normalizatsiyalangan kalitlar.
 * @param {{ saleUnit?: unknown; saleOptions?: Array<{ saleUnit?: unknown; nestedSaleLines?: Array<{ saleUnit?: unknown }> }> } | null | undefined} root
 * @returns {string[]}
 */
export function collectNormalizedSaleUnitKeysFromForm(root) {
  if (!root) return [];
  const seen = new Set();
  const out = [];
  const add = (raw) => {
    const n = normalizeSaleUnit(raw);
    if (n === 'size') return;
    if (seen.has(n)) return;
    seen.add(n);
    out.push(n);
  };
  add(root.saleUnit);
  for (const o of root.saleOptions || []) {
    add(o?.saleUnit);
    for (const nl of o?.nestedSaleLines || []) add(nl?.saleUnit);
  }
  return out;
}


export function normalizeSaleUnit(raw) {
  const u = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (!u) return SALE_UNIT_PIECE;
  if (u === 'kg' || u === 'kilogram' || u === 'кг' || u === 'kilo') return SALE_UNIT_KG;
  if (
    u === 'meter' ||
    u === 'metre' ||
    u === 'metr' ||
    u === 'm' ||
    u === 'м' ||
    u === 'mtr'
  )
    return SALE_UNIT_METER;
  if (u === 'l' || u === 'ltr' || u === 'liter' || u === 'litre' || u === 'литр') return SALE_UNIT_LITER;
  if (u === 'ton' || u === 't' || u === 'tonna' || u === 'тонна') return SALE_UNIT_TON;
  if (u === 'm3' || u === 'm^3' || u === 'куб' || u === 'cubic_meter' || u === 'm³') return SALE_UNIT_CUBIC_METER;
  if (u === 'm2' || u === 'm^2' || u === 'sqm' || u === 'm²') return 'sqm';
  if (u === 'cm' || u === 'sm' || u === 'сm' || u === 'см') return SALE_UNIT_CM;
  if (u === 'mm' || u === 'миллиметр' || u === 'мм') return SALE_UNIT_MM;
  if (u === 'pair' || u === 'juft' || u === 'para' || u === 'пара') return SALE_UNIT_PAIR;
  if (u === 'size' || u === 'razmer' || u === 'o\'lcham' || u === 'olcham') return SALE_UNIT_SIZE;
  if (u === 'box' || u === 'quti') return 'box';
  if (u === 'pack' || u === 'pachka') return 'pack';
  if (u === 'roll' || u === 'rulon') return 'roll';
  if (u === 'set' || u === 'toplam' || u === 'to‘plam' || u === 'to' || u === "to'plam") return 'set';
  if (u === 'pallet' || u === 'palet') return 'pallet';
  if (u === 'wholesale' || u === 'optom' || u === 'bulk' || u === 'улгуржи') return SALE_UNIT_WHOLESALE;
  return u;
}

/**
 * @param {string} unit
 */
export function isWeightedSaleUnit(unit) {
  return WEIGHTED.has(unit);
}

/**
 * @param {string} unit
 */
export function minQtyForUnit(unit) {
  return isWeightedSaleUnit(unit) ? 0.001 : 1;
}

/**
 * @param {string} unit
 */
export function qtyStepForUnit(unit) {
  return isWeightedSaleUnit(unit) ? 0.1 : 1;
}

/**
 * @param {{ price?: number; qty?: number; lineDiscountAmount?: number }} item
 */
export function cartLineTotal(item) {
  const p = Number(item?.price) || 0;
  const q = Number(item?.qty) || 0;
  const gross = Math.round(p * q);
  const disc = Math.max(0, Math.round(Number(item?.lineDiscountAmount) || 0));
  return Math.max(0, gross - disc);
}

/**
 * Bir xil mahsulotni savatda qo‘shish: kg/m uchun og‘irlik narxini og‘irlik bo‘yicha o‘rtacha qiymatlash.
 * @param {{ price: number; qty: number }} existing
 * @param {{ price: number; qty: number }} incoming
 */
export function mergeWeightedCartLine(existing, incoming) {
  const q1 = Math.max(0, Number(existing.qty) || 0);
  const q2 = Math.max(0, Number(incoming.qty) || 0);
  const p1 = Math.max(0, Number(existing.price) || 0);
  const p2 = Math.max(0, Number(incoming.price) || 0);
  const newQty = q1 + q2;
  if (newQty <= 0) return { qty: q2, price: p2 };
  const newPrice = Math.round((p1 * q1 + p2 * q2) / newQty);
  return { qty: Math.round(newQty * 1000) / 1000, price: newPrice };
}
